/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Subject } from "rxjs";

// PrimeNG imports
import { MenuItem } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DialogService } from "primeng/dynamicdialog";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { Menu, MenuModule } from "primeng/menu";
import { Popover, PopoverModule } from "primeng/popover";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import {
  TableModule,
  TableRowCollapseEvent,
  TableRowExpandEvent,
} from "primeng/table";
import { TagModule } from "primeng/tag";
import { TooltipModule } from "primeng/tooltip";
import { SkeletonModule } from "primeng/skeleton";
import {
  TableSkeletonRowsComponent,
  SkeletonColumnConfig,
  SKELETON_PRESETS,
} from "../../../../shared/components/table-skeleton";
// DrawerModule removed - bottom sheet feature disabled (redundant with expanded row)

// Our services
import { Site } from "../../../../core/models/site.interface";
import { TimezoneHelper } from "../../../../core/utils/timezone.helper";
import { ProcessingErrorCode } from "../../../settings/interfaces/email-settings.interface";
import { ProcessingErrorCodesHelper } from "../../../settings/utils/processing-error-codes.helper";

// Dialog components
import { ScheduledOrderHistoryDialogComponent } from "../scheduled-order-history-dialog/scheduled-order-history-dialog.component";
import { ScheduledOrderCyclesDialogComponent } from "../scheduled-order-cycles-dialog/scheduled-order-cycles-dialog.component";
import { ValidationChecksDialogComponent } from "../validation-checks-dialog/validation-checks-dialog.component";
import { ProcessingCycleLogsDialogComponent } from "../processing-cycle-logs-dialog/processing-cycle-logs-dialog.component";

// Our interfaces
import {
  ScheduledOrderStatus,
  PAGE_SIZE_OPTIONS,
} from "../../interfaces/scheduled-orders-filter.interface";
import {
  DEFAULT_TABLE_COLUMNS,
  PagedResult,
  PageEvent,
  RowAction,
  ScheduledOrderListItem,
  SortEvent,
  TableColumn,
} from "../../interfaces/scheduled-orders-list-item.interface";

// Bulk actions interfaces
import {
  BulkActionEvent,
  BulkActionsToolbarComponent,
} from "../bulk-actions-toolbar/bulk-actions-toolbar.component";
import { ScheduledOrderQuickActionsComponent } from "../scheduled-order-quick-actions/scheduled-order-quick-actions.component";
import { ScheduledOrder } from "../../../../core/models/scheduled-order.interface";
import { SwipeDirective, SwipeEvent } from "../../directives/swipe.directive";

/**
 * Scheduled Orders Table Component
 * PrimeNG p-table implementation with bulk actions and row menus
 */
@Component({
  selector: "app-scheduled-orders-table",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    CheckboxModule,
    MenuModule,
    PopoverModule,
    TagModule,
    TooltipModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    SkeletonModule,
    IconFieldModule,
    InputIconModule,
    BulkActionsToolbarComponent,
    ScheduledOrderQuickActionsComponent,
    SwipeDirective,
    TableSkeletonRowsComponent,
  ],
  templateUrl: "./scheduled-orders-table.component.html",
  styleUrl: "./scheduled-orders-table.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DialogService],
})
export class ScheduledOrdersTableComponent
  implements OnInit, OnDestroy, OnChanges
{
  // Input/Output pattern
  @Input() data: PagedResult<ScheduledOrderListItem> | null = null;
  @Input() loading = false;
  @Input() columns: TableColumn[] = DEFAULT_TABLE_COLUMNS;
  @Input() showCheckboxes = true;
  @Input() showActions = true;
  @Input() virtualScrolling = false;
  @Input() scrollHeight = "flex";
  @Input() optimizedLayout = false; // Enable optimized column and space usage
  @Input() responsiveLayout: "stack" | "scroll" = "scroll"; // Using scroll to allow custom mobile card layout
  @Input() breakpoint: string = "768px"; // Breakpoint for responsive layout
  @Input() site: Site | null = null; // Site data for timezone calculations
  @Input() accessToken?: any; // Access token for embedded mode dialogs
  @Input() embeddedMode: boolean = false; // Whether running in embedded/web component mode
  // Items 8 & 9: Cache of items counts per order (orderId -> {enabled, total})
  @Input() itemsCounts: Map<number, { enabled: number; total: number }> =
    new Map();
  @Input() set selection(value: ScheduledOrderListItem[]) {
    if (value) {
      this.selectedItems = [...value];
    }
  }

  @Output() sortChanged = new EventEmitter<SortEvent>();
  @Output() pageChanged = new EventEmitter<PageEvent>();
  @Output() selectionChanged = new EventEmitter<ScheduledOrderListItem[]>();
  @Output() rowAction = new EventEmitter<{
    action: string;
    item: ScheduledOrderListItem;
  }>();
  @Output() bulkAction = new EventEmitter<BulkActionEvent>();
  @Output() navigateToCycle = new EventEmitter<{
    processingCycleId: number;
    scheduledOrderId: number;
    siteId: number;
  }>();
  @Input() bulkLoading = false;

  // ViewChild references
  @ViewChild("contextMenu") contextMenu!: Menu;
  @ViewChild("timezoneOverlay") timezoneOverlay!: Popover;

  // Component state
  private readonly destroy$ = new Subject<void>();

  selectedItems: ScheduledOrderListItem[] = [];
  globalFilter = "";

  // Row expansion state
  expandedRows: { [key: string]: boolean } = {};

  // Row actions configuration
  rowActions: RowAction[] = [];
  contextMenuItems: MenuItem[] = [];
  currentContextItem: ScheduledOrderListItem | null = null;
  timezoneDetails: string = "";

  // Expose page size options for template
  readonly pageSizeOptions = [...PAGE_SIZE_OPTIONS];

  // Skeleton columns configuration
  readonly skeletonColumns: SkeletonColumnConfig[] =
    SKELETON_PRESETS.scheduledOrders;

  // Swipe gesture state
  swipedItemId: number | null = null;
  swipeDirection: "left" | "right" | null = null;
  swipeOffset: number = 0;

  // Bottom sheet state removed - feature disabled (redundant with expanded row)

  constructor(
    private timezoneHelper: TimezoneHelper,
    private processingErrorCodesHelper: ProcessingErrorCodesHelper,
    private dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.initializeRowActions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["data"] && this.data) {
      // Clear selections when data changes
      this.selectedItems = [];
      this.selectionChanged.emit(this.selectedItems);
    }
  }

  /**
   * Initialize row actions based on old QMC functionality with Phase 3 status-based refinements
   *
   * Action Matrix (Phase 3):
   * - Active: Pause, Process Now, Reschedule, Cancel/Delete, View / Edit, View Event Logs, View Cycles History
   * - Failed: Activate, Restart Processing, Process Now, Reschedule, Cancel/Delete, View / Edit, View Event Logs, View Cycles History
   * - Paused: Activate, Process Now, Reschedule, Cancel/Delete, View / Edit, View Event Logs, View Cycles History
   * - Processing: View / Edit, View Event Logs, View Cycles History
   * - Deleted: Activate, View / Edit, View Event Logs, View Cycles History
   * - Queued: Activate, View / Edit, View Event Logs, View Cycles History
   */
  private initializeRowActions(): void {
    this.rowActions = [
      // Always available actions (all statuses)
      {
        label: "View Event Logs",
        icon: "pi pi-info",
        command: (item) => this.openHistoryDialog(item),
      },
      {
        label: "View Cycles History",
        icon: "pi pi-history",
        command: (item) => {
          return this.openCyclesDialog(item);
        },
      },

      // View / Edit - available for all statuses
      {
        label: "View / Edit",
        icon: "pi pi-pencil",
        command: (item) => this.rowAction.emit({ action: "edit", item }),
      },

      // Status-specific actions (Phase 3 refinements)
      {
        label: "Activate",
        icon: "pi pi-play",
        command: (item) => this.rowAction.emit({ action: "activate", item }),
        visible: (item) =>
          item.Status === "Failed" ||
          item.Status === "Paused" ||
          item.Status === "Deleted" ||
          item.Status === "Queued",
      },
      {
        label: "Pause",
        icon: "pi pi-pause",
        command: (item) => this.rowAction.emit({ action: "pause", item }),
        visible: (item) => item.Status === "Active",
      },
      {
        label: "Process Now",
        icon: "pi pi-refresh",
        command: (item) => this.rowAction.emit({ action: "processNow", item }),
        visible: (item) =>
          item.Status === "Active" ||
          item.Status === "Paused" ||
          item.Status === "Failed",
      },
      {
        label: "Restart Processing",
        icon: "pi pi-refresh",
        command: (item) => this.rowAction.emit({ action: "retry", item }),
        visible: (item) => item.Status === "Failed",
      },
      {
        label: "Retry Update Order",
        icon: "pi pi-refresh",
        command: (item) => this.rowAction.emit({ action: "retryUpdate", item }),
        visible: (item) =>
          item.Status === "Failed" && item.ProcessingErrorCode === 3001,
      },
      {
        label: "Reschedule",
        icon: "pi pi-calendar",
        command: (item) => this.rowAction.emit({ action: "reschedule", item }),
        visible: (item) =>
          item.Status === "Active" ||
          item.Status === "Failed" ||
          item.Status === "Paused",
      },
      {
        label: "Cancel/Delete",
        icon: "pi pi-times",
        command: (item) => this.rowAction.emit({ action: "delete", item }),
        visible: (item) =>
          item.Status === "Active" ||
          item.Status === "Failed" ||
          item.Status === "Paused",
      },
    ];
  }

  /**
   * Handle table sorting
   */
  onSort(event: any): void {
    const sortEvent: SortEvent = {
      field: event.field,
      order: event.order,
      multisortmeta: event.multisortmeta,
    };
    this.sortChanged.emit(sortEvent);
  }

  /**
   * Handle table pagination
   */
  onPageChange(event: any): void {
    const pageEvent: PageEvent = {
      first: event.first,
      rows: event.rows,
      page: event.page,
      pageCount: event.pageCount,
    };
    this.pageChanged.emit(pageEvent);
  }

  /**
   * Handle selection changes
   */
  onSelectionChange(): void {
    this.selectionChanged.emit(this.selectedItems);
  }

  /**
   * Toggle select all/none
   */
  toggleSelectAll(): void {
    if (this.isAllSelected()) {
      this.selectedItems = [];
    } else {
      this.selectedItems = [...(this.data?.Items || [])];
    }
    this.selectionChanged.emit(this.selectedItems);
  }

  /**
   * Check if all items are selected
   */
  isAllSelected(): boolean {
    return (
      (this.data?.Items?.length ?? 0) > 0 &&
      this.selectedItems.length === (this.data?.Items?.length ?? 0)
    );
  }

  /**
   * Check if some items are selected (for indeterminate state)
   */
  isSomeSelected(): boolean {
    return (
      this.selectedItems.length > 0 &&
      this.selectedItems.length < (this.data?.Items?.length || 0)
    );
  }

  /**
   * Get row actions menu items for a specific item
   */
  getRowMenuItems(item: ScheduledOrderListItem): MenuItem[] {
    const filteredActions = this.rowActions.filter((action) => {
      const isVisible = !action.visible || action.visible(item);
      return isVisible;
    });

    const menuItems = filteredActions.map((action) => ({
      label: action.label,
      icon: action.icon,
      command: action.command ? () => action.command!(item) : undefined,
      disabled: action.disabled ? action.disabled(item) : false,
      separator: action.separator || false,
    }));

    return menuItems;
  }

  /**
   * Get status badge severity based on status
   */
  getStatusSeverity(
    status: ScheduledOrderStatus,
  ): "success" | "warn" | "danger" | "info" | "secondary" | "contrast" {
    switch (status) {
      case "Active":
        return "success";
      case "Paused":
        return "warn";
      case "Failed":
        return "danger";
      case "Processing":
        return "info";
      case "Queued":
        return "info";
      case "Locked":
        return "secondary";
      case "Deleted":
        return "secondary";
      default:
        return "secondary";
    }
  }

  /**
   * Get processing error description using ProcessingErrorCodesHelper
   */
  getProcessingErrorDescription(code?: number): string {
    if (!code) return "";
    return (
      this.processingErrorCodesHelper.getTextFromProcessingCode(
        code as ProcessingErrorCode,
      ) || `Error Code ${code}`
    );
  }

  /**
   * Get documentation URL for processing error code
   */
  getProcessingErrorDocumentationUrl(code?: number): string | null {
    if (!code) return null;
    return this.processingErrorCodesHelper.getDocumentationUrlFromProcessingErrorCode(
      code as ProcessingErrorCode,
    );
  }

  /**
   * Format currency values
   */
  formatCurrency(
    value: number | null | undefined,
    currencyIso = "USD",
  ): string {
    if (value == null) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyIso,
      minimumFractionDigits: 2,
    }).format(value);
  }

  /**
   * Format date values with timezone support
   */
  formatDate(date: Date | string | null | undefined): string {
    if (!date) return "-";
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  /**
   * Format date with site timezone - matches old QMC dateFormat pipe
   */
  formatDateWithSiteTimezone(
    date: Date | string | null | undefined,
    siteUtcOffset?: number,
  ): string {
    if (!date) return "-";
    const offset = siteUtcOffset ?? 0;
    return this.timezoneHelper.formatDateWithTimezone(date, offset);
  }

  /**
   * Get timezone tooltip for date fields - matches old QMC complex tooltip
   */
  getDateTimezoneTooltip(
    date: Date | string | null | undefined,
    item: ScheduledOrderListItem,
    fieldName: string,
  ): string {
    const siteOffset = this.site?.UtcOffset ?? 0;
    const customerOffset = item.UtcOffset ?? 0;
    return this.timezoneHelper.createTimezoneTooltip(
      date,
      siteOffset,
      customerOffset,
      fieldName,
    );
  }

  /**
   * Get formatted GMT display - matches old QMC getFormattedGMT method
   */
  getFormattedGmt(utcOffset?: number): string {
    return this.timezoneHelper.formatGmtOffset(utcOffset, "GMT");
  }

  /**
   * Format frequency display
   */
  formatFrequency(frequency: number, frequencyType: string): string {
    if (!frequency || !frequencyType) return "-";

    const unit = frequency === 1 ? frequencyType.slice(0, -1) : frequencyType;
    return `Every ${frequency} ${unit}`;
  }

  /**
   * Format frequency in a compact single-line format
   * Item 11: Display explicit weekday names for DayOfTheWeek
   */
  formatFrequencyCompact(frequency: number, frequencyType: string): string {
    if (!frequency || !frequencyType) return "-";

    // Item 11: Handle DayOfTheWeek - show actual weekday name
    if (frequencyType === "DayOfTheWeek" || frequencyType === "DayOfWeek") {
      const weekdays = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      // API uses 0-6 (Sunday-Saturday)
      const dayIndex = frequency % 7;
      return weekdays[dayIndex] || `Day ${frequency}`;
    }

    // Handle DayOfMonth - show ordinal
    if (frequencyType === "DayOfMonth" || frequencyType === "DayOfTheMonth") {
      return `${frequency}${this.getOrdinalSuffix(frequency)} of mo`;
    }

    // Convert to singular/plural unit
    const unit = frequency === 1 ? frequencyType.slice(0, -1) : frequencyType;

    // For common cases, use abbreviations
    const abbreviations: Record<string, string> = {
      Day: "d",
      Days: "d",
      Week: "wk",
      Weeks: "wks",
      Month: "mo",
      Months: "mos",
      Year: "yr",
      Years: "yrs",
    };

    const abbr = abbreviations[unit];
    if (abbr) {
      return `${frequency} ${abbr}`;
    }

    // Fallback: add spaces to camelCase (DayOfTheWeek -> Day Of The Week)
    const formatted = frequencyType.replace(/([A-Z])/g, " $1").trim();
    return `${frequency} ${formatted}`;
  }

  /**
   * Get ordinal suffix for a number (1st, 2nd, 3rd, etc.)
   */
  private getOrdinalSuffix(n: number): string {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }

  /**
   * Format items count for display
   * Items 8 & 9: Shows "X of Y" format where X is enabled items count, Y is total items count
   * Examples: "3 of 5" (3 enabled, 5 total items), "5 of 5" (all enabled)
   * Note: This uses ITEM COUNT (number of product lines), not QUANTITY SUM
   */
  formatItemsCount(item: ScheduledOrderListItem | null | undefined): string {
    if (!item) return "-";

    // Items 8 & 9: Check if we have the counts from the cache (calculated from full order data)
    if (this.itemsCounts.has(item.Id)) {
      const counts = this.itemsCounts.get(item.Id)!;
      return `${counts.enabled} of ${counts.total}`;
    }

    // Fallback: If no cached data yet, show total quantity (will update when data loads)
    const totalItems = item.TotalProductQuantity ?? 0;
    return `${totalItems}`;
  }

  /**
   * Format frequency type for readable display
   * Converts camelCase to spaced words (DayOfTheWeek -> Day Of The Week)
   */
  formatFrequencyType(frequencyType: string): string {
    if (!frequencyType) return "-";
    // Add spaces before capital letters and trim
    return frequencyType.replace(/([A-Z])/g, " $1").trim();
  }

  /**
   * Get validation checks display
   */
  getValidationChecksDisplay(item: ScheduledOrderListItem): {
    icon: string;
    severity: "success" | "warning" | "danger";
    tooltip: string;
  } {
    if (!item?.ValidationChecks) {
      return {
        icon: "pi pi-question-circle",
        severity: "warning",
        tooltip: "Validation not run",
      };
    }

    if (item.ValidationChecks.Succeeded) {
      return {
        icon: "pi pi-check-circle",
        severity: "success",
        tooltip: "All validation checks passed",
      };
    }

    const errorCount = item.ValidationChecks.ErrorItems?.length || 0;
    return {
      icon: "pi pi-exclamation-triangle",
      severity: "danger",
      tooltip: `${errorCount} validation error${errorCount !== 1 ? "s" : ""}`,
    };
  }

  /**
   * Check if item should show dunning indicator
   */
  shouldShowDunningIndicator(item: ScheduledOrderListItem): boolean {
    return item?.IsInDunning || false;
  }

  /**
   * Check if item should show locked indicator
   */
  shouldShowLockedIndicator(item: ScheduledOrderListItem): boolean {
    return item?.Locked || false;
  }

  /**
   * Get last processing cycle status display - matches old QMC column
   */
  getLastProcessingCycleStatus(item: ScheduledOrderListItem): string {
    let status = item?.LastProcessingCycle?.Status || "";
    if (item?.IsInDunning) {
      status += " In Dunning";
    }
    return status;
  }

  /**
   * Check if status should show processing spinner - matches old QMC
   */
  shouldShowProcessingSpinner(status: ScheduledOrderStatus): boolean {
    return status === "Processing";
  }

  /**
   * Check if status should show queued timer icon - matches old QMC
   */
  shouldShowQueuedIcon(status: ScheduledOrderStatus): boolean {
    return status === "Queued";
  }

  /**
   * Get customer full name
   */
  getCustomerFullName(item: ScheduledOrderListItem): string {
    const firstName = item?.CustomerFirstName?.trim() || "";
    const lastName = item?.CustomerLastName?.trim() || "";

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    }
    return item?.CustomerEmail || "-";
  }

  /**
   * Get shipping address display
   */
  getShippingAddressDisplay(item: ScheduledOrderListItem): string {
    const parts = [
      item?.ShippingPostcode,
      item?.ShippingState,
      item?.ShippingCountry,
    ].filter(Boolean);

    return parts.join(", ") || "-";
  }

  /**
   * Track by function for ngFor performance
   */
  trackByItemId(index: number, item: ScheduledOrderListItem): number {
    return item?.Id || 0;
  }

  /**
   * Track by function for columns ngFor performance
   */
  trackByColumn(index: number, column: any): string {
    return column.field;
  }

  /**
   * Check if column should be displayed
   */
  isColumnVisible(column: TableColumn): boolean {
    if (column.field === "Checkbox" && !this.showCheckboxes) {
      return false;
    }
    if (column.field === "Actions" && !this.showActions) {
      return false;
    }
    if (column.field === "Indicators") {
      return true; // Always show Indicators column
    }
    if (column.field === "ValidationChecks") {
      return true; // Always show ValidationChecks column
    }
    return true;
  }

  /**
   * Handle row double-click to view details
   */
  onRowDoubleClick(event: any): void {
    this.rowAction.emit({ action: "view", item: event.data });
  }

  /**
   * Open validation checks dialog - matches old QMC PreProcessingResultIndicator functionality
   */
  openValidationDialog(item: ScheduledOrderListItem): void {
    if (!this.site) return;

    const ref = this.dialogService.open(ValidationChecksDialogComponent, {
      header: `Validation Checks - Order #${item.Id}`,
      width: "700px",
      modal: true,
      closable: true,
      closeOnEscape: true,
      dismissableMask: false,
      breakpoints: {
        "960px": "90vw",
        "640px": "95vw",
      },
      data: {
        scheduledOrder: item,
        site: this.site,
        dontPersistPreValidationRefresh: false,
        accessToken: this.accessToken, // Pass access token for embedded mode
      },
    });

    ref.onClose.subscribe((result: ScheduledOrderListItem) => {
      if (result) {
        // Update the item in the parent component
        this.rowAction.emit({ action: "validationUpdated", item: result });
      }
    });
  }

  /**
   * Open processing history dialog - matches old QMC ScheduledOrderHistoryDialogComponent functionality
   */
  openHistoryDialog(item: ScheduledOrderListItem): void {
    if (!this.site) return;

    this.dialogService.open(ScheduledOrderHistoryDialogComponent, {
      header: "Event Logs",
      width: "90vw",
      modal: true,
      closable: true,
      closeOnEscape: true,
      breakpoints: {
        "960px": "90vw",
        "640px": "95vw",
      },
      style: {
        "max-width": "1000px",
      },
      data: {
        scheduledOrderId: item.Id,
        siteId: this.site.Id,
        scheduledOrder: item,
        accessToken: this.accessToken, // Pass access token for embedded mode
      },
    });
  }

  /**
   * Open processing cycles dialog - matches old QMC ScheduledOrderCyclesDialogComponent functionality
   */
  openCyclesDialog(item: ScheduledOrderListItem): void {
    if (!this.site) return;

    const isMobile = window.innerWidth <= 768;
    const dialogRef = this.dialogService.open(
      ScheduledOrderCyclesDialogComponent,
      {
        header: "Processing Cycles History",
        width: isMobile ? "95vw" : "80vw",
        height: isMobile ? "85vh" : "70vh",
        modal: true,
        closable: true,
        closeOnEscape: true,
        dismissableMask: true,
        maximizable: true,
        breakpoints: {
          "960px": "90vw",
          "640px": "95vw",
        },
        contentStyle: {
          overflow: "auto",
          padding: isMobile ? "0.75rem" : "1rem",
        },
        style: {
          "max-width": "1200px",
        },
        data: {
          scheduledOrderId: item.Id,
          siteId: this.site.Id,
          scheduledOrder: item,
          accessToken: this.accessToken, // Pass access token for embedded mode
          embeddedMode: this.embeddedMode, // Pass embedded mode to dialog
        },
      },
    );

    // Listen for navigation events from dialog (for embedded mode)
    if (dialogRef.onClose) {
      dialogRef.onClose.subscribe((result: any) => {
        if (
          result?.action === "navigate-to-cycle" &&
          result.processingCycleId
        ) {
          this.navigateToCycle.emit({
            processingCycleId: result.processingCycleId,
            scheduledOrderId: result.scheduledOrderId,
            siteId: result.siteId,
          });
        }
      });
    }
  }

  /**
   * Open processing logs dialog for specific cycle - matches old QMC ScheduledOrderLogsDialogComponent functionality
   */
  openProcessingLogsDialog(item: ScheduledOrderListItem): void {
    if (!this.site || !item.LastProcessingCycle) return;

    const processingId =
      item.LastProcessingCycle.ProcessingId || item.LastProcessingCycle.Id;

    this.dialogService.open(ProcessingCycleLogsDialogComponent, {
      header: `Processing Cycle ${processingId} Logs`,
      width: "80vw",
      modal: true,
      closable: true,
      closeOnEscape: true,
      dismissableMask: false,
      breakpoints: {
        "960px": "90vw",
        "640px": "98vw",
      },
      style: {
        "max-width": "1400px",
        "max-height": "90vh",
      },
      data: {
        scheduledOrderId: item.Id,
        siteId: this.site.Id,
        processingCycleId: item.LastProcessingCycle.Id,
        processingCycle: item.LastProcessingCycle,
        accessToken: this.accessToken, // Pass access token for embedded mode
      },
    });
  }

  /**
   * Show timezone details - matches old QMC timezone display functionality
   */
  showTimezoneDetails(
    event: Event,
    item: ScheduledOrderListItem,
    dateField: string,
  ): void {
    if (!this.site) return;

    const dateValue =
      dateField === "NextOccurrenceUtc" ? item.NextOccurrenceUtc : null;
    if (!dateValue) return;

    this.timezoneDetails = this.getDateTimezoneTooltip(
      dateValue,
      item,
      "Scheduled Order Next Occurrence",
    );

    this.timezoneOverlay.toggle(event);
  }

  /**
   * Toggle row menu
   */
  toggleRowMenu(event: Event, item: ScheduledOrderListItem): void {
    this.currentContextItem = item;
    this.contextMenuItems = this.getRowMenuItems(item);

    try {
      this.contextMenu.toggle(event);
    } catch (error) {
      console.error("Error calling contextMenu.toggle:", error);
    }
  }

  /**
   * Track by function for validation check names (missing in HTML template)
   */
  trackByCheckName(index: number, check: any): string {
    return check.name || index.toString();
  }

  /**
   * Event handler for row expansion - sync expandedRows state with PrimeNG
   */
  onRowExpand(event: TableRowExpandEvent): void {
    const item = event.data as ScheduledOrderListItem;
    // Single row mode - only one row expanded at a time
    this.expandedRows = { [item.Id.toString()]: true };
  }

  /**
   * Event handler for row collapse - sync expandedRows state with PrimeNG
   */
  onRowCollapse(event: TableRowCollapseEvent): void {
    const item = event.data as ScheduledOrderListItem;
    delete this.expandedRows[item.Id.toString()];
    this.expandedRows = { ...this.expandedRows }; // Trigger change detection
  }

  /**
   * Toggle row expansion (used for desktop row click-to-expand)
   * Clicking on row (except interactive elements) expands/collapses the row
   */
  toggleRowExpansion(item: ScheduledOrderListItem): void {
    const key = item.Id.toString();
    if (this.expandedRows[key]) {
      delete this.expandedRows[key];
      this.expandedRows = { ...this.expandedRows }; // Trigger change detection
    } else {
      // Single expansion mode - clear others first
      this.expandedRows = { [key]: true };
    }
  }

  /**
   * Get primary table columns for main row
   */
  getPrimaryColumns(): TableColumn[] {
    return [
      {
        field: "Id",
        header: "ID",
        sortable: true,
        width: "auto",
        minWidth: "4rem",
      },
      {
        field: "Status",
        header: "Status",
        sortable: true,
        width: "auto",
        minWidth: "8rem",
      },
      {
        field: "ValidationChecks",
        header: "Validation",
        sortable: false,
        width: "auto",
        minWidth: "5rem",
      },
      {
        field: "Indicators",
        header: "Indicators",
        sortable: false,
        width: "auto",
        minWidth: "5rem",
      },
      {
        field: "CustomerFirstName",
        header: "Customer",
        sortable: true,
        width: "auto",
        minWidth: "8rem",
      },
      {
        field: "CustomerEmail",
        header: "Customer Email",
        sortable: true,
        width: "auto",
        minWidth: "12rem",
      },
      {
        field: "LastProcessingCycle",
        header: "Last Cycle Status",
        sortable: false,
        width: "auto",
        minWidth: "8rem",
      },
      {
        field: "LastOccurrenceUtc",
        header: "Last Processed",
        sortable: true,
        width: "auto",
        minWidth: "10rem",
      },
      {
        field: "NextOccurrenceUtc",
        header: "Next Occurrence",
        sortable: true,
        width: "auto",
        minWidth: "10rem",
      },
      {
        field: "Cycles",
        header: "Billing Cycle",
        sortable: true,
        width: "auto",
        minWidth: "7rem",
      },
      {
        field: "Frequency",
        header: "Frequency",
        sortable: true,
        width: "auto",
        minWidth: "7rem",
      },
    ];
  }

  /**
   * Get secondary columns for expanded row
   */
  getSecondaryColumns(): TableColumn[] {
    return this.columns.filter(
      (col) =>
        !this.getPrimaryColumns().some(
          (primary) => primary.field === col.field,
        ) &&
        col.field !== "Actions" &&
        col.field !== "Checkbox",
    );
  }

  /**
   * Convert list item to full ScheduledOrder for quick actions component
   */
  convertToScheduledOrder(item: ScheduledOrderListItem): ScheduledOrder {
    return {
      Id: item.Id,
      Status: item.Status,
      CustomerFirstName: item.CustomerFirstName,
      CustomerLastName: item.CustomerLastName,
      CustomerEmail: item.CustomerEmail,
      NextOccurrenceUtc: item.NextOccurrenceUtc,
      Cycles: item.Cycles,
      Frequency: item.Frequency,
      FrequencyType: item.FrequencyType,
      LastProcessingCycle: item.LastProcessingCycle,
      // Required fields with defaults
      FreeShipping: {
        OptionValue: "",
        OptionText: "",
        IsDefault: false,
      },
      Coupons: "",
      CouponsHistory: [],
      ProcessingErrorCode: item.ProcessingErrorCode ?? 0,
      ShippingRateOptions: [],
      Metadata: item.Metadata ?? {},
      PreValidateSynchronously: false,
      Locked: item.Locked ?? false,
      LastEditableDate: item.LastEditableDate ?? new Date(),
      DisplaySalePrice: false,
      IsInDunning: item.IsInDunning ?? false,
      FallbackToQpilotShippingWhenNextimeFails: false,
    } as ScheduledOrder;
  }

  /**
   * Handle quick action clicks from expanded row
   */
  onQuickAction(event: { action: string; order: ScheduledOrder }): void {
    const item = this.data?.Items?.find((i) => i.Id === event.order.Id);
    if (item) {
      this.rowAction.emit({ action: event.action, item });
    }
  }

  /**
   * Check if quick actions should be shown for this status
   * Show for all statuses except Processing (which has no actions)
   */
  shouldShowQuickActions(status?: ScheduledOrderStatus): boolean {
    return (
      status === "Active" ||
      status === "Failed" ||
      status === "Paused" ||
      status === "Deleted" ||
      status === "Queued"
    );
  }

  // ============================================
  // SWIPE GESTURE HANDLERS
  // ============================================

  /**
   * Handle swipe left gesture - reveals danger actions (Edit, Delete)
   */
  onSwipeLeft(event: SwipeEvent, item: ScheduledOrderListItem): void {
    this.swipedItemId = item.Id;
    this.swipeDirection = "left";
    this.swipeOffset = -80; // Reveal right-side actions
  }

  /**
   * Handle swipe right gesture - triggers primary action based on status
   */
  onSwipeRight(event: SwipeEvent, item: ScheduledOrderListItem): void {
    // Execute primary action based on status
    if (item.Status === "Failed") {
      // Retry for failed orders
      this.rowAction.emit({ action: "retry", item });
    } else if (item.Status === "Active") {
      // Process now for active orders
      this.rowAction.emit({ action: "processNow", item });
    } else if (item.Status === "Paused") {
      // Activate for paused orders
      this.rowAction.emit({ action: "activate", item });
    }

    // Reset swipe state
    this.resetSwipeState();
  }

  /**
   * Handle swipe move for visual feedback
   */
  onSwipeMove(distance: number, item: ScheduledOrderListItem): void {
    this.swipedItemId = item.Id;
    // Clamp the offset to reasonable bounds
    this.swipeOffset = Math.max(-100, Math.min(100, distance));
  }

  /**
   * Reset swipe state when gesture ends
   */
  onSwipeEnd(item: ScheduledOrderListItem): void {
    // Only reset if swipe wasn't far enough to trigger action
    if (Math.abs(this.swipeOffset) < 50) {
      this.resetSwipeState();
    }
  }

  /**
   * Reset all swipe state
   */
  resetSwipeState(): void {
    this.swipedItemId = null;
    this.swipeDirection = null;
    this.swipeOffset = 0;
  }

  /**
   * Check if item is currently swiped
   */
  isItemSwiped(itemId: number): boolean {
    return this.swipedItemId === itemId && this.swipeDirection !== null;
  }

  /**
   * Get swipe action label based on item status
   */
  getSwipeActionLabel(status?: ScheduledOrderStatus): string {
    switch (status) {
      case "Failed":
        return "Retry";
      case "Active":
        return "Process";
      case "Paused":
        return "Activate";
      default:
        return "";
    }
  }

  /**
   * Get swipe action icon based on item status
   */
  getSwipeActionIcon(status?: ScheduledOrderStatus): string {
    switch (status) {
      case "Failed":
        return "pi pi-refresh";
      case "Active":
        return "pi pi-play";
      case "Paused":
        return "pi pi-play";
      default:
        return "pi pi-arrow-right";
    }
  }

  /**
   * Execute swipe action from revealed button
   */
  executeSwipeAction(item: ScheduledOrderListItem, action: string): void {
    this.rowAction.emit({ action, item });
    this.resetSwipeState();
  }

  // Bottom sheet methods removed - feature disabled (redundant with expanded row)
  // Quick actions toggle methods removed - quick actions now only shown in expanded row
}
